import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {RootState, useAppDispatch} from "../../../../redux/store";
import {
    deletePrevisione,
    fetchPrevisioni,
    fetchPrevisioniTutte,
    Previsione,
    PrevisioniList,
    FASE_COLLEZIONE,
    UNITA_MISURA_PREVISIONI
} from "./previsioniSlice";
import path from "../../../../../static/images/logo/logo-Everest.png"
import {
    FiltriPrevisioni,
    setFiltriPrevisioni,
    resetFiltriPrevisioni
} from "components/userPreference/userPreferenceSlice";
import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import {Routes_path} from "components/routerApp";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import InventoryIcon from "@mui/icons-material/Inventory";
import DeleteIcon from "@mui/icons-material/Delete";
import PrevisioniPageBreadcrumbs from "./PrevisioniPageBreadcrumbs";
import {Soggetto, Tecnica, Commerciale, Stagione} from "components/anagrafiche/anagraficheSlice";
import {OutlinedInput, SelectChangeEvent} from "@mui/material";
import {theme} from "../../../theme";
import PrevisioniRiga from "./PrevisioniRiga";
import {pdf} from "@react-pdf/renderer";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ListinoPDF from "./ListinoPDF";
import {getStatoCollezioneColor} from "../../evopCommon/getStatoColor";

// ============================================
// COMPONENT
// ============================================

const PrevisioniPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // ============================================
    // SELECTORS - Filtri da userPreference!
    // ============================================

    const previsioni = useSelector<RootState, PrevisioniList>(
        (state) => state.previsioniState.previsioniList
    );

    const filtri = useSelector<RootState, FiltriPrevisioni>(
        (state) => state.userPreferenceState.filtriPrevisioni || {}
    );

    // Anagrafiche per filtri
    const soggetti = useSelector<RootState, Soggetto[]>(
        (state) => state.anagraficheState?.soggetti?.results || []
    );
    const mandatari = soggetti.filter(m => m.is_mandatario);
    const brands = soggetti.filter(m => m.is_brand);
    const confezionisti = soggetti.filter(m => m.is_confezionista);

    const stagioni = useSelector<RootState, Stagione[]>(
        (state) => state.anagraficheState?.stagioni?.results || []
    );

    const commerciali = useSelector<RootState, Commerciale[]>(
        (state) => state.anagraficheState?.commerciali?.results || []
    );

    const tecniche = useSelector<RootState, Tecnica[]>(
        (state) => state.anagraficheState?.tecniche?.results || []
    );

    // ============================================
    // STATI LOCALI - Solo per UI temporanea
    // ============================================

    const [searchValue, setSearchValue] = useState("");
    const [refresh, setRefresh] = useState(false);

    // Stati e tecnica (multipli)
    const [statiSelezionati, setStatiSelezionati] = useState<string[]>(
        filtri?.fase_collezione || []
    );
    const [tecnicheSelezionate, setTecnicheSelezionate] = useState<number[]>(
        filtri?.tecnica || []
    );

    // Filtri singoli
    const [mandatarioSelezionato, setMandatarioSelezionato] = useState<number | undefined>(
        filtri?.mandatario
    );
    const [brandSelezionato, setBrandSelezionato] = useState<number | undefined>(
        filtri?.brand
    );
    const [confezionistaSelezionato, setConfezionistaSelezionato] = useState<number | undefined>(
        filtri?.confezionista
    );
    const [stagioneSelezionata, setStagioneSelezionata] = useState<number | undefined>(
        filtri?.stagione
    );
    const [commercialeSelezionato, setCommercialeSelezionato] = useState<number | undefined>(
        filtri?.commerciale
    );

    // Selezione previsioni per PDF
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [includiImpianto, setIncludiImpianto] = useState(false);

    // Menu anchors
    const [anchorElStatoFilter, setAnchorElStatoFilter] = useState<null | HTMLElement>(null);
    const [anchorElTecnicaFilter, setAnchorElTecnicaFilter] = useState<null | HTMLElement>(null);

    // ============================================
    // EFFECTS
    // ============================================

    // Caricamento iniziale con filtri salvati
    useEffect(() => {
        dispatch(fetchPrevisioni(filtri));
    }, []);

    // Refresh quando richiesto
    useEffect(() => {
        if (refresh) {
            const nuoviFiltri: FiltriPrevisioni = {
                ...filtri,
                search: searchValue || undefined,
                fase_collezione: statiSelezionati.length > 0 ? statiSelezionati : undefined,
                tecnica: tecnicheSelezionate.length > 0 ? tecnicheSelezionate : undefined,
                mandatario: mandatarioSelezionato,
                brand: brandSelezionato,
                confezionista: confezionistaSelezionato,
                stagione: stagioneSelezionata,
                commerciale: commercialeSelezionato,
                page: 1,  // Reset a pagina 1 quando si applicano filtri
            };

            dispatch(setFiltriPrevisioni(nuoviFiltri));
            stagioneSelezionata && dispatch(fetchPrevisioni(nuoviFiltri));
            setSelectedIds(new Set());
            setRefresh(false);
        }
    }, [refresh]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleSearchSubmit = () => {
        setRefresh(true);
    };

    const handleResetFiltri = () => {
        // Reset stati locali
        setSearchValue("");
        setStatiSelezionati(['PROT', 'CAMP', 'AN']);
        setTecnicheSelezionate([]);
        setMandatarioSelezionato(undefined);
        setBrandSelezionato(undefined);
        setConfezionistaSelezionato(undefined);
        setStagioneSelezionata(undefined);
        setCommercialeSelezionato(undefined);

        // Reset selezione PDF
        setSelectedIds(new Set());

        // Reset in userPreference
        dispatch(resetFiltriPrevisioni());

        // Fetch con filtri default
        const filtriDefault = {
            numero_record: 20,
            page: 1,
            fase_collezione: ['PROT', 'CAMP', 'AN']
        };
        dispatch(setFiltriPrevisioni(filtriDefault));
        dispatch(fetchPrevisioni(filtriDefault));
    };

    const handlePaginationChange = (event: React.ChangeEvent<unknown>, page: number) => {
        const nuoviFiltri = {...filtri, page};
        dispatch(setFiltriPrevisioni(nuoviFiltri));
        dispatch(fetchPrevisioni(nuoviFiltri));
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const handleRecordsPerPageChange = (event: SelectChangeEvent<number>) => {
        const numero_record = event.target.value as number;
        const nuoviFiltri = {
            ...filtri,
            numero_record,
            page: 1  // Reset a pagina 1 quando cambia il numero di record
        };
        dispatch(setFiltriPrevisioni(nuoviFiltri));
        dispatch(fetchPrevisioni(nuoviFiltri));
    };

    const toggleStato = (stato: string) => {
        setStatiSelezionati(prev =>
            prev.includes(stato)
                ? prev.filter(s => s !== stato)
                : [...prev, stato]
        );
        setRefresh(true)
    };

    const toggleTecnica = (tecnicaId: number) => {
        setTecnicheSelezionate(prev =>
            prev.includes(tecnicaId)
                ? prev.filter(t => t !== tecnicaId)
                : [...prev, tecnicaId]
        );
        setRefresh(true)
    };

    // Selezione per PDF
    const handleToggleSelect = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleToggleSelectAll = () => {
        const visibleIds = previsioni.results.map(p => p.id!).filter(Boolean);
        const allVisibleSelected = visibleIds.every(id => selectedIds.has(id));

        if (allVisibleSelected) {
            // Deseleziona tutti i visibili
            setSelectedIds(prev => {
                const next = new Set(prev);
                visibleIds.forEach(id => next.delete(id));
                return next;
            });
        } else {
            // Seleziona tutti i visibili
            setSelectedIds(prev => {
                const next = new Set(prev);
                visibleIds.forEach(id => next.add(id));
                return next;
            });
        }
    };

    const visibleIds = previsioni.results.map(p => p.id!).filter(Boolean);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    const someVisibleSelected = visibleIds.some(id => selectedIds.has(id));

    const handleDelete = async (previsione: Previsione) => {
        if (window.confirm(`Eliminare la previsione ${previsione.codice_item}?`)) {
            await dispatch(deletePrevisione({previsioneToDelete: previsione}));
            dispatch(fetchPrevisioni(filtri));
        }
    };

    // ============================================
    // PULSANTE STAMPA PDF
    // ============================================

    const [pdfLoading, setPdfLoading] = useState(false);

    // Condizione per mostrare il pulsante (visibile quando filtri attivi, abilitato solo con selezione)
    const mostraPulsanteStampa =
        stagioneSelezionata !== undefined &&
        mandatarioSelezionato !== undefined &&
        statiSelezionati.includes('CAMP') &&
        previsioni.results.length > 0;

    // Dati per il PDF
    const stagionePerPDF = stagioni.find(s => s.id === stagioneSelezionata);
    const mandatarioPerPDF = mandatari.find(m => m.id === mandatarioSelezionato);

    const nomeFilePDF = mostraPulsanteStampa && stagionePerPDF && mandatarioPerPDF
        ? `Listino_${stagionePerPDF.descrizione}_${mandatarioPerPDF.nome}.pdf`
            .replace(/\s+/g, '_')
            .replace(/[^a-zA-Z0-9_.-]/g, '')
        : 'listino.pdf';

    const handleDownloadPDF = async () => {
        if (!stagionePerPDF || !mandatarioPerPDF || selectedIds.size === 0) return;

        setPdfLoading(true);
        try {
            // Fetch tutte le previsioni (senza paginazione) e filtra per selectedIds
            const tuttePrevisioni = await dispatch(fetchPrevisioniTutte(filtri)).unwrap();
            const previsioniFiltrate = tuttePrevisioni.results.filter(
                (p: Previsione) => p.id && selectedIds.has(p.id)
            );

            const blob = await pdf(
                <ListinoPDF
                    stagione={stagionePerPDF.descrizione}
                    mandatario={mandatarioPerPDF.nome}
                    previsioni={previsioniFiltrate}
                    mostraImpianto={includiImpianto}
                />
            ).toBlob();

            // Crea link e scarica
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = nomeFilePDF;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Errore generazione PDF:', error);
        } finally {
            setPdfLoading(false);
        }
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box sx={{p: 3}}>
            {/* Header */}
            <Grid container spacing={2} sx={{mb: 1}}>
                <Grid size={{xs: 12, sm: 6}}>
                    <Stack direction="row" spacing={2} sx={{justifyContent: "flex-start", alignItems: "flex-start",}}>
                        <PrevisioniPageBreadcrumbs/>
                    </Stack>

                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <FormControl sx={{m: 1, minWidth: 250, width: '70%'}} variant="outlined">
                            <InputLabel htmlFor="outlined-adornment-password">Ricerca</InputLabel>
                            <OutlinedInput
                                id="outlined-adornment-ricerca"
                                type='text'
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={(e) => {
                                                setRefresh(true);
                                            }}
                                            // onMouseDown={}
                                            // onMouseUp={}
                                            edge="end"
                                        >
                                            <SearchIcon/>
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Ricerca"
                                placeholder="Codice, descrizione, articolo..."
                                value={searchValue}
                                onChange={(e) => {
                                    setSearchValue(e.target.value)
                                    // setRefresh(true);
                                }}
                                onBlur={() => setRefresh(true)}
                            />
                        </FormControl>

                        {/* PULSANTE STAMPA PDF con react-pdf */}
                        {mostraPulsanteStampa && stagionePerPDF && mandatarioPerPDF && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={includiImpianto}
                                            onChange={(e) => setIncludiImpianto(e.target.checked)}
                                        />
                                    }
                                    label={<Typography variant="body2">Includi Impianto</Typography>}
                                    sx={{mr: 0, textAlign: 'center'}}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<PictureAsPdfIcon/>}
                                    onClick={handleDownloadPDF}
                                    disabled={pdfLoading || selectedIds.size === 0}
                                >
                                    {pdfLoading
                                        ? 'Generazione...'
                                        : `Stampa Listino PDF${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`
                                    }
                                </Button>
                            </>
                        )}

                        <Button
                            variant="contained"
                            startIcon={<AddIcon/>}
                            component={Link}
                            to={Routes_path.PREVISIONE}
                            state={{
                                stagione: filtri.stagione,
                                commerciale: filtri.commerciale,
                                mandatario: filtri.mandatario,
                                brand: filtri.brand,
                                confezionista: filtri.confezionista,
                            }}
                        >
                            Nuova Voce Listino
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {/* Filtri Card */}
            <Card sx={{marginBottom: theme.spacing(2), padding: theme.spacing(2),}}>
                <Grid container spacing={2} alignItems="center">


                    <Grid size={{xs: 12, md: 2.3}}>
                        <Autocomplete
                            value={stagioni.find(s => s.id === stagioneSelezionata) || null}
                            options={stagioni}
                            getOptionLabel={(option) => option.descrizione || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.descrizione}
                                </li>
                            )}
                            onChange={(_, newValue) => {
                                setStagioneSelezionata(newValue?.id)
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField {...params}
                                           sx={{backgroundColor: stagioneSelezionata ? theme.palette.action.selected : 'inherit'}}
                                           label="Stagione"/>
                            )}
                        />
                    </Grid>


                    <Grid size={{xs: 12, md: 4, lg: 2.3}}>
                        <Autocomplete
                            value={mandatari.find(m => m.id === mandatarioSelezionato) || null}
                            options={mandatari}
                            getOptionLabel={(option) => option.nome || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.nome}
                                </li>
                            )}
                            onChange={(_, newValue) => {
                                setMandatarioSelezionato(newValue?.id)
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField {...params}
                                           sx={{backgroundColor: mandatarioSelezionato ? theme.palette.action.selected : 'inherit'}}
                                           label="Mandatario"/>
                            )}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 4, lg: 2.3}}>
                        <Autocomplete
                            value={brands.find(b => b.id === brandSelezionato) || null}
                            options={brands}
                            getOptionLabel={(option) => option.nome || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.nome}
                                </li>
                            )}
                            onChange={(_, newValue) => {
                                setBrandSelezionato(newValue?.id)
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField {...params}
                                           sx={{backgroundColor: brandSelezionato ? theme.palette.action.selected : 'inherit'}}
                                           label="Brand"/>
                            )}
                        />
                    </Grid>

                    {/* Seconda riga - Altri filtri */}
                    <Grid size={{xs: 12, md: 2.3}}>
                        <Autocomplete
                            value={confezionisti.find(c => c.id === confezionistaSelezionato) || null}
                            options={confezionisti}
                            getOptionLabel={(option) => option.nome || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.nome}
                                </li>
                            )}
                            onChange={(_, newValue) => {
                                setConfezionistaSelezionato(newValue?.id)
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField {...params}
                                           sx={{backgroundColor: confezionistaSelezionato ? theme.palette.action.selected : 'inherit'}}
                                           label="Confezionista"/>
                            )}
                        />
                    </Grid>


                    <Grid size={{xs: 12, md: 2.3}}>
                        <Autocomplete
                            value={commerciali.find(c => c.id === commercialeSelezionato) || null}
                            options={commerciali}
                            getOptionLabel={(option) => option.user_display || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.user_display}
                                </li>
                            )}
                            onChange={(_, newValue) => {
                                setCommercialeSelezionato(newValue?.id)
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField {...params}
                                           sx={{backgroundColor: commercialeSelezionato ? theme.palette.action.selected : 'inherit'}}
                                           label="Commerciale"/>
                            )}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 0.5}}>
                        <IconButton onClick={handleResetFiltri} color="primary">
                            <FilterAltOffIcon/>
                        </IconButton>
                    </Grid>

                    {/* Chips stati e tecniche selezionati */}
                    <Grid size={{xs: 12}}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                            {statiSelezionati.map(stato => (
                                <Chip
                                    key={stato}
                                    label={FASE_COLLEZIONE[stato as keyof typeof FASE_COLLEZIONE]}
                                    color={getStatoCollezioneColor(stato)}
                                    size="small"
                                    onDelete={() => {
                                        toggleStato(stato);
                                        setRefresh(true);
                                    }}
                                />
                            ))}
                            {tecnicheSelezionate.map(tecnicaId => {
                                const tecnica = tecniche.find(t => t.id === tecnicaId);
                                return tecnica ? (
                                    <Chip
                                        key={tecnicaId}
                                        label={tecnica.nome}
                                        color="secondary"
                                        size="small"
                                        onDelete={() => toggleTecnica(tecnicaId)}
                                    />
                                ) : null;
                            })}
                        </Stack>
                    </Grid>
                </Grid>
            </Card>

            {/* Header tabella */}
            {stagioneSelezionata &&

                <Card sx={{backgroundColor: theme.palette.grey[50], marginBottom: theme.spacing(1),}}>
                    <CardContent sx={{py: 1}}>
                        <Grid container spacing={2}>
                            {mostraPulsanteStampa && (
                                <Grid size={{xs: 0.5, lg: 0.3}}>
                                    <Checkbox
                                        size="small"
                                        checked={allVisibleSelected}
                                        indeterminate={someVisibleSelected && !allVisibleSelected}
                                        onChange={handleToggleSelectAll}
                                    />
                                </Grid>
                            )}
                            <Grid size={{xs: 1, lg: 0.5}}>
                                <Typography variant="subtitle2" fontWeight="bold">Stagione</Typography>
                            </Grid>
                            <Grid size={{xs: 2, lg: 1}}>
                                <Typography variant="subtitle2" fontWeight="bold">Mandatario</Typography>
                            </Grid>
                            <Grid size={{xs: 2, lg: 1}}>
                                <Typography variant="subtitle2" fontWeight="bold">Codice</Typography>
                            </Grid>
                            <Grid size={{xs: 3, lg: mostraPulsanteStampa ? 2.7 : 3}}>
                                <Typography variant="subtitle2" fontWeight="bold">Descrizione</Typography>
                            </Grid>
                            <Grid size={{xs: 2, lg: 1}}>
                                <Typography variant="subtitle2" fontWeight="bold">Brand</Typography>
                            </Grid>
                            <Grid size={{xs: 2, lg: 1}}>
                                <Typography variant="subtitle2" fontWeight="bold">Confezionista</Typography>
                            </Grid>

                            <Grid size={{xs: 1, lg: 1}}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle2" fontWeight="bold">Tecnica</Typography>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setAnchorElTecnicaFilter(e.currentTarget)}
                                        sx={{color: tecnicheSelezionate.length > 0 ? "secondary.main" : "inherit"}}
                                    >
                                        <FilterAltIcon fontSize="small"/>
                                    </IconButton>
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 1, lg: 0.5}}>
                                <Typography variant="subtitle2" fontWeight="bold">Prezzo</Typography>
                            </Grid>
                            <Grid size={{xs: 2, lg: 1.5}}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle2" fontWeight="bold">Fase Collezione</Typography>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setAnchorElStatoFilter(e.currentTarget)}
                                        sx={{color: statiSelezionati.length > 0 ? "secondary.main" : "inherit"}}
                                    >
                                        <FilterAltIcon fontSize="small"/>
                                    </IconButton>
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 1, lg: 1}}>
                                <Typography variant="subtitle2" fontWeight="bold">Q.tà BDG</Typography>
                            </Grid>

                        </Grid>
                    </CardContent>
                </Card>
            }

            {/* Lista */}
            {stagioneSelezionata &&

                <Box>
                    {previsioni.results.map((previsione) => (
                        <PrevisioniRiga
                            key={previsione.id}
                            previsione={previsione}
                            onDelete={handleDelete}
                            selectable={mostraPulsanteStampa}
                            selected={previsione.id ? selectedIds.has(previsione.id) : false}
                            onToggleSelect={handleToggleSelect}
                        />
                    ))}

                    {/* Paginazione e selezione numero record */}

                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3}}>
                        <FormControl sx={{minWidth: 180}} size="small">
                            <InputLabel>Record per pagina</InputLabel>
                            <Select
                                value={filtri.numero_record || 20}
                                label="Record per pagina"
                                onChange={handleRecordsPerPageChange}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                        {previsioni.num_pages > 1 && (
                            <Pagination
                                count={previsioni.num_pages}
                                page={filtri.page || 1}
                                onChange={handlePaginationChange}
                                color="primary"
                            />
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Totale: {previsioni.count} record
                        </Typography>
                    </Box>


                    {/* Empty state */}
                    {previsioni.results.length === 0 &&
                        previsioni.errorsStack.status !== ErrorStatusTypes.PENDING && (
                            <Card>
                                <CardContent>
                                    <Box sx={{textAlign: 'center', py: 4}}>
                                        <InventoryIcon sx={{fontSize: 64, color: 'grey.400', mb: 2}}/>
                                        <Typography variant="h6" color="text.secondary">
                                            Nessuna previsione trovata
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Modifica i filtri o crea una nuova previsione
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                </Box>
            }
            {/* Menu filtro stati */}
            <Menu
                anchorEl={anchorElStatoFilter}
                open={Boolean(anchorElStatoFilter)}
                onClose={() => setAnchorElStatoFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {Object.entries(FASE_COLLEZIONE).map(([key, value]) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    checked={statiSelezionati.includes(key)}
                                    onChange={() => toggleStato(key)}
                                />
                            }
                            label={value}
                        />
                    ))}
                </FormGroup>
                <Box sx={{px: 2, pb: 1}}>
                    <Button
                        onClick={() => {
                            setAnchorElStatoFilter(null);
                            setRefresh(true);
                        }}
                        variant="contained"
                        size="small"
                        fullWidth
                    >
                        Applica
                    </Button>
                </Box>
            </Menu>

            {/* Menu filtro tecniche */
            }
            <Menu
                anchorEl={anchorElTecnicaFilter}
                open={Boolean(anchorElTecnicaFilter)}
                onClose={() => setAnchorElTecnicaFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {tecniche.map((tecnica) => (
                        <FormControlLabel
                            key={tecnica.id}
                            control={
                                <Checkbox
                                    checked={tecnicheSelezionate.includes(tecnica.id!)}
                                    onChange={() => toggleTecnica(tecnica.id!)}
                                />
                            }
                            label={tecnica.nome}
                        />
                    ))}
                </FormGroup>
                <Box sx={{px: 2, pb: 1}}>
                    <Button
                        onClick={() => {
                            setAnchorElTecnicaFilter(null);
                            setRefresh(true);
                        }}
                        variant="contained"
                        size="small"
                        fullWidth
                    >
                        Applica
                    </Button>
                </Box>
            </Menu>

            {/* Loading */
            }
            <Backdrop
                open={previsioni.errorsStack.status === ErrorStatusTypes.PENDING}
                sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>
        </Box>
    )
        ;
};

export default PrevisioniPage;